/**
 * Bill Deposit Detail Page
 *
 * In-layout result/detail view for a per-bill deposit.
 * Reached after creating a deposit and from the bill's deposit history.
 * Offers a print action that opens the thermal receipt.
 */

import { useQuery } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, CheckCircle, FileText, Printer, Receipt } from 'lucide-react'

import api from '@/lib/api'
import { formatCurrency } from '@/lib/utils'
import { t } from '@/i18n'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'

interface DepositReceiptData {
  business_name: string
  deposit_id: number
  bill_id: number
  bill_number: string | null
  client_name: string
  bill_total: number
  bill_created_at: string
  deposit_amount: number
  total_deposited: number
  total_debts: number
  payment_type: string
  deposit_created_at: string
  cashier_name: string
}

export function BillDepositDetailPage() {
  const { id, depositId } = useParams<{ id: string; depositId: string }>()
  const navigate = useNavigate()
  const billId = id && !isNaN(Number(id)) ? Number(id) : 0

  const { data: deposit, isLoading } = useQuery<DepositReceiptData>({
    queryKey: ['bill-deposit-receipt', billId, depositId],
    queryFn: async () => {
      const response = await api.get(`/bills/${billId}/deposits/${depositId}/receipt`)
      return response.data
    },
    enabled: !!billId && !!depositId,
  })

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-48" />
        </div>
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (!deposit) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Receipt className="h-12 w-12 text-muted-foreground/50 mb-4" />
        <p className="text-lg font-medium mb-2">{t('receipts.notFound')}</p>
        <Button variant="outline" onClick={() => navigate(`/bills/${billId}`)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('bills.backToBills')}
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4 pb-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10 shrink-0"
          onClick={() => navigate(`/bills/${billId}`)}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl md:text-2xl font-bold truncate">
            {t('bills.depositAdded')}
          </h1>
          <p className="text-sm text-muted-foreground truncate">
            {t('bills.billNumber')}: {deposit.bill_number || `#${deposit.bill_id}`}
          </p>
        </div>
      </div>

      {/* Amount Summary */}
      <Card className="border-green-200 dark:border-green-900/40 bg-green-50/50 dark:bg-green-950/20">
        <CardContent className="pt-6">
          <div className="flex items-center gap-3 mb-4">
            <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
            <div>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                {formatCurrency(deposit.deposit_amount)}
              </p>
              <p className="text-sm text-muted-foreground">{t('deposits.depositSuccess')}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-lg bg-background border text-center">
              <p className="text-xs text-muted-foreground mb-1">{t('bills.totalDeposited')}</p>
              <p className="text-lg font-bold text-green-600 dark:text-green-400">
                {formatCurrency(deposit.total_deposited)}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-background border text-center">
              <p className="text-xs text-muted-foreground mb-1">{t('bills.remainingDebt')}</p>
              <p
                className={`text-lg font-bold ${deposit.total_debts > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}
              >
                {formatCurrency(deposit.total_debts)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Deposit + Bill Details */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-4 w-4" />
            {t('bills.billInfo')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t('bills.client')}</span>
            <span className="font-medium">{deposit.client_name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t('bills.total')}</span>
            <span className="font-medium">{formatCurrency(deposit.bill_total)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t('deposits.paymentType')}</span>
            <span className="font-medium">{deposit.payment_type}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t('bills.date')}</span>
            <span className="font-medium">{deposit.deposit_created_at}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t('deposits.cashier')}</span>
            <span className="font-medium">{deposit.cashier_name}</span>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="space-y-3 pt-2">
        <Button
          className="h-12 w-full"
          onClick={() => navigate(`/receipts/deposit/${billId}/${deposit.deposit_id}`)}
        >
          <Printer className="h-4 w-4 mr-2" />
          {t('deposits.printReceipt')}
        </Button>
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            variant="outline"
            className="h-12 sm:flex-1"
            onClick={() => navigate(`/bills/${billId}`)}
          >
            <Receipt className="h-4 w-4 mr-2" />
            {t('bills.viewBill')}
          </Button>
        </div>
      </div>
    </div>
  )
}
